import type { CustomFile } from '../types';

import type { TypeUploadConfig, TypeResolvedFeatures } from './types';
import type { TypeUploadContext } from './createUploadContext';

import throttle from '../../timing/throttle';

// ----------------------------------------------------------------------------

export type TypeUploadProgress = {
    setExpected: (file: CustomFile, bytes: number) => void;
    setLoaded: (file: CustomFile, bytes: number) => void;
    markUploaded: (file: CustomFile, bytes: number) => void;
    markDone: (file: CustomFile) => void;
    emit: () => void;
    flush: () => void;
};

// ----------------------------------------------------------------------------

/*
 * Byte-weighted progress for one upload run: each file contributes its own bytes, so a large
 * file moves the total more than a small one, and a run where no file has a known size falls
 * back to counting completions. Emission is throttled, with `flush` for the final state.
 */
function createUploadProgress(
    files: CustomFile[],
    config: TypeUploadConfig,
    context: TypeUploadContext,
    features: TypeResolvedFeatures,
): TypeUploadProgress {
    const loadedByFile = new Map<CustomFile, number>();
    const expectedByFile = new Map<CustomFile, number>();

    for (const file of files) {
        loadedByFile.set(file, 0);
        expectedByFile.set(file, Math.max(0, file.size ?? 0));
    }

    //
    const fnGetTotalPercentByBytes = function (): number {
        if (!context.totalFiles) {
            return 0;
        }

        let sumLoaded = 0;
        let sumExpected = 0;

        for (const file of files) {
            const expected = expectedByFile.get(file) ?? 0;

            if (expected <= 0) {
                continue;
            }

            const loaded = loadedByFile.get(file) ?? 0;

            sumLoaded += Math.min(expected, Math.max(0, loaded));
            sumExpected += expected;
        }

        // No file carries known bytes (e.g. all zero-byte): fall back to file completion.
        if (!sumExpected) {
            return (context.completedFiles.value / context.totalFiles) * 100;
        }

        return (sumLoaded / sumExpected) * 100;
    };

    //
    const fnEmit = function (): void {
        const totalPercent = fnGetTotalPercentByBytes();

        config.fnProgressUpload?.(null, {
            file: null,
            totalPercent,
            currentFile: context.completedFiles.value,
            errorCount: context.errorCount,
            successCount: context.successCount,
            skippedCount: context.skippedCount,
            totalFiles: context.totalFiles,
            aborted: context.abortedFlag.aborted,
        });
    };

    /*
     * With the feature off nothing is emitted, but the byte bookkeeping below still runs: the
     * orchestrators read those counters to decide when a file is done, so they are not optional.
     */
    const throttledEmit = features.progress.enabled
        ? throttle(fnEmit, features.progress.throttleMs)
        : Object.assign((): void => undefined, { cancel: (): void => undefined });

    return {
        setExpected: function (file, bytes): void {
            expectedByFile.set(file, Math.max(0, bytes));
        },
        setLoaded: function (file, bytes): void {
            const expected = expectedByFile.get(file) ?? 0;
            // Cap at expected so XHR FormData overhead (> file.size) never inflates the high-water mark.
            const capped = Math.min(expected, Math.max(0, bytes));
            const current = loadedByFile.get(file) ?? 0;
            // Never go backwards — retries start a new XHR at 0 but progress must not regress.
            loadedByFile.set(file, Math.max(current, capped));
        },
        markUploaded: function (file, bytes): void {
            loadedByFile.set(file, Math.max(0, bytes));
        },
        /*
         * Marks a terminally-processed file (success or definitive error) as fully accounted,
         * so total progress reaches 100% once every file is done instead of stalling on failures.
         */
        markDone: function (file): void {
            loadedByFile.set(file, expectedByFile.get(file) ?? 0);
        },
        emit: throttledEmit,
        // Drops any pending throttled call and emits the final state immediately.
        flush: function (): void {
            throttledEmit.cancel();

            if (features.progress.enabled && context.totalFiles > 0) {
                fnEmit();
            }
        },
    };
}

// ----------------------------------------------------------------------------

export default createUploadProgress;
